import { apiFetch } from './client';

// Member-cards client. Owner / administrator manage templates; any logged
// in member can issue / rotate / view their own card via the
// /me/card flow (the handler enforces self-only at the service layer).

export type FieldKind =
  | 'static_text'
  | 'member_name'
  | 'member_id'
  | 'fiscal_year'
  | 'qr'
  | 'org_name'
  | 'org_logo';

export interface CardTemplateField {
  kind: FieldKind;
  x: number;
  y: number;
  font_size: number;
  weight?: number;
  color?: string;
  static_text?: string;
  i18n_key?: string;
}

export type BackgroundKind = 'color' | 'image' | 'gradient';
export type PIILevel = 'full_name' | 'first_name';
export type YearBinding = 'current_year' | 'frozen_at_issue';

export interface CardTemplate {
  id: string;
  name: string;
  is_default: boolean;
  background_kind: BackgroundKind;
  background_color: string;
  background_image_object_key?: string;
  background_gradient_color2?: string;
  background_gradient_angle: number;
  text_color: string;
  accent_color: string;
  font_heading: string;
  font_body: string;
  fields: CardTemplateField[];
  public_pii_level: PIILevel;
  show_member_id: boolean;
  year_binding: YearBinding;
  content_offset_y: number;
  content_offset_x: number;
  created_at: string;
  updated_at: string;
}

export interface CardPass {
  id: string;
  member_id: string;
  template_id: string;
  fiscal_year: number | null;
  public_token: string;
  public_url: string;
  public_token_expires_at?: string;
  created_at: string;
}

export interface CardRender {
  template: CardTemplate;
  pass: CardPass;
  svg: string;
  status: {
    fiscal_year: number;
    paid: boolean;
    invoice_number: string;
    currency: string;
    amount_minor: number;
    paid_at: string | null;
  };
}

export async function listTemplates(token: string | null, slug: string): Promise<{ templates: CardTemplate[] }> {
  return apiFetch(`/api/v1/organizations/${encodeURIComponent(slug)}/membercard-templates`, { token });
}

export async function getTemplate(token: string | null, slug: string, id: string): Promise<CardTemplate> {
  return apiFetch(`/api/v1/organizations/${encodeURIComponent(slug)}/membercard-templates/${id}`, { token });
}

export type CreateTemplatePayload = Omit<CardTemplate, 'id' | 'created_at' | 'updated_at'>;
export type UpdateTemplatePayload = Partial<Omit<CardTemplate, 'id' | 'created_at' | 'updated_at' | 'is_default'>>;

export async function createTemplate(
  token: string | null,
  slug: string,
  payload: CreateTemplatePayload,
): Promise<CardTemplate> {
  return apiFetch(`/api/v1/organizations/${encodeURIComponent(slug)}/membercard-templates`, {
    method: 'POST',
    body: payload,
    token,
  });
}

export async function updateTemplate(
  token: string | null,
  slug: string,
  id: string,
  patch: UpdateTemplatePayload,
): Promise<CardTemplate> {
  return apiFetch(`/api/v1/organizations/${encodeURIComponent(slug)}/membercard-templates/${id}`, {
    method: 'PATCH',
    body: patch,
    token,
  });
}

export async function deleteTemplate(token: string | null, slug: string, id: string): Promise<void> {
  await apiFetch(`/api/v1/organizations/${encodeURIComponent(slug)}/membercard-templates/${id}`, {
    method: 'DELETE',
    token,
  });
}

export async function setDefaultTemplate(token: string | null, slug: string, id: string): Promise<void> {
  await apiFetch(`/api/v1/organizations/${encodeURIComponent(slug)}/membercard-templates/${id}/set-default`, {
    method: 'POST',
    token,
  });
}

// Per-member pass operations.

export async function issuePass(
  token: string | null,
  slug: string,
  memberID: string,
  payload?: { template_id?: string; fiscal_year?: number },
): Promise<CardPass> {
  return apiFetch(`/api/v1/organizations/${encodeURIComponent(slug)}/members/${memberID}/membercard/issue`, {
    method: 'POST',
    body: payload ?? {},
    token,
  });
}

export async function rotatePass(token: string | null, slug: string, memberID: string): Promise<CardPass> {
  return apiFetch(`/api/v1/organizations/${encodeURIComponent(slug)}/members/${memberID}/membercard/rotate`, {
    method: 'POST',
    token,
  });
}

export async function getPass(token: string | null, slug: string, memberID: string): Promise<CardPass> {
  return apiFetch(`/api/v1/organizations/${encodeURIComponent(slug)}/members/${memberID}/membercard`, { token });
}

export async function renderCard(token: string | null, slug: string, memberID: string): Promise<CardRender> {
  return apiFetch(`/api/v1/organizations/${encodeURIComponent(slug)}/members/${memberID}/membercard/render`, {
    token,
  });
}
